// ---------------------------------------------------------------------------
// Landing "descend into the ocean" sections: reveal-on-scroll, scroll-down
// buttons, and a lightweight rising-bubble canvas per deep section.
// ---------------------------------------------------------------------------

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Add `.revealed` to `el` the first time it scrolls into view. Falls back to
// revealing immediately if IntersectionObserver is unavailable.
export function revealOnView(el: Element, threshold = 0.35): void {
  if (typeof IntersectionObserver !== "function") {
    el.classList.add("revealed");
    return;
  }
  const obs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          el.classList.add("revealed");
          obs.disconnect();
          break;
        }
      }
    },
    { threshold },
  );
  obs.observe(el);
}

// Wire every `[data-scroll-to="#id"]` button to smooth-scroll to its target.
export function wireScrollButtons(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>("[data-scroll-to]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const sel = btn.getAttribute("data-scroll-to");
      if (!sel) return;
      const target = document.querySelector(sel);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });
}

interface Bubble {
  stream: number;
  y: number;
  rad: number;
  speed: number;
  wobAmp: number;
  wobFreq: number;
  phase: number;
  alpha: number;
}

export interface BubbleController {
  start: () => void;
  stop: () => void;
}

// Rising-bubble field on a 2D canvas. Returns a controller whose start/stop
// gate the RAF loop (driven by a visibility observer). No-ops under reduced
// motion or when the 2D context is unavailable.
export function createBubbles(canvas: HTMLCanvasElement): BubbleController {
  const noop: BubbleController = { start: () => {}, stop: () => {} };
  if (prefersReducedMotion()) return noop;
  const cx = canvas.getContext("2d");
  if (!cx) return noop;

  let w = 0;
  let h = 0;
  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = Math.max(1, Math.floor(rect.width));
    h = Math.max(1, Math.floor(rect.height));
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    cx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  resize();
  window.addEventListener("resize", resize);

  const numStreams = 3 + Math.floor(Math.random() * 3); // 3..5
  const streams: number[] = [];
  for (let s = 0; s < numStreams; s++) {
    const frac =
      (s + 0.5) / numStreams + (Math.random() - 0.5) * (0.4 / numStreams);
    streams.push(Math.min(0.94, Math.max(0.06, frac)));
  }

  const count = numStreams * 9;
  const bubbles: Bubble[] = [];
  function spawn(b: Bubble, atBottom: boolean): void {
    b.y = atBottom ? h + Math.random() * 60 : Math.random() * h;
    const r = Math.random();
    b.rad = 0.6 + r * r * 2.6;
    b.speed = 14 + b.rad * 6 + Math.random() * 12;
    b.wobAmp = 1.5 + Math.random() * 3.5;
    b.wobFreq = 0.6 + Math.random() * 1.4;
    b.phase = Math.random() * Math.PI * 2;
    b.alpha = 0.18 + Math.random() * 0.32;
  }
  for (let i = 0; i < count; i++) {
    const b: Bubble = {
      stream: Math.floor(Math.random() * numStreams),
      y: 0,
      rad: 0,
      speed: 0,
      wobAmp: 0,
      wobFreq: 0,
      phase: 0,
      alpha: 0,
    };
    spawn(b, false);
    bubbles.push(b);
  }

  let raf = 0;
  let last = 0;
  let running = false;
  function frame(now: number): void {
    if (!running) return;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
    last = now;
    cx!.clearRect(0, 0, w, h);
    for (const b of bubbles) {
      b.y -= b.speed * dt;
      b.phase += b.wobFreq * dt;
      const x = streams[b.stream]! * w + Math.sin(b.phase) * b.wobAmp;
      if (b.y < -b.rad - 2) {
        spawn(b, true);
        continue;
      }
      cx!.beginPath();
      cx!.arc(x, b.y, b.rad, 0, Math.PI * 2);
      cx!.fillStyle = `rgba(120, 210, 235, ${(b.alpha * 0.5).toFixed(3)})`;
      cx!.fill();
      cx!.lineWidth = Math.max(0.5, b.rad * 0.18);
      cx!.strokeStyle = `rgba(180, 235, 255, ${b.alpha.toFixed(3)})`;
      cx!.stroke();
    }
    raf = requestAnimationFrame(frame);
  }

  return {
    start() {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(frame);
    },
    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    },
  };
}

// A small bumpy ocean floor with scattered stones and a few critters scurrying
// along it — a subtle seabed at the very bottom of the page. Returns a
// controller; under reduced motion it draws a static floor once and never loops.
export function createOceanFloor(canvas: HTMLCanvasElement): BubbleController {
  const noop: BubbleController = { start: () => {}, stop: () => {} };
  const cx = canvas.getContext("2d");
  if (!cx) return noop;

  let w = 0;
  let h = 0;
  function size(): void {
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    w = Math.max(1, Math.floor(rect.width));
    h = Math.max(1, Math.floor(rect.height));
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    cx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // Floor surface profile (summed sines), evaluated in CSS pixels.
  function floorY(x: number): number {
    return (
      h * 0.42 +
      Math.sin(x * 0.012 + 0.7) * (h * 0.05) +
      Math.sin(x * 0.031 + 2.1) * (h * 0.025) +
      Math.sin(x * 0.07 + 4.0) * (h * 0.012)
    );
  }

  interface Stone {
    frac: number;
    rw: number;
    rh: number;
    rot: number;
    shade: number;
  }
  const stones: Stone[] = [];
  for (let i = 0; i < 12 + Math.floor(Math.random() * 5); i++) {
    stones.push({
      frac: Math.random(),
      rw: 2 + Math.random() * 6,
      rh: 1.4 + Math.random() * 2.6,
      rot: (Math.random() - 0.5) * 0.6,
      shade: Math.random(),
    });
  }

  interface Critter {
    x: number;
    dir: number;
    speed: number;
    len: number;
    leg: number;
    timer: number;
    moving: boolean;
    gait: number;
  }
  const critters: Critter[] = [];
  function buildCritters(): void {
    critters.length = 0;
    for (let i = 0; i < 4 + Math.floor(Math.random() * 3); i++) {
      critters.push({
        x: Math.random() * w,
        dir: Math.random() < 0.5 ? -1 : 1,
        speed: 12 + Math.random() * 20,
        len: 2.2 + Math.random() * 2.4,
        leg: Math.random() * Math.PI * 2,
        timer: 0.3 + Math.random() * 1.2,
        moving: Math.random() < 0.7,
        gait: 1,
      });
    }
  }

  function drawFloor(): void {
    cx!.clearRect(0, 0, w, h);
    cx!.beginPath();
    cx!.moveTo(0, floorY(0));
    for (let x = 0; x <= w; x += 8) cx!.lineTo(x, floorY(x));
    cx!.lineTo(w, h);
    cx!.lineTo(0, h);
    cx!.closePath();
    const g = cx!.createLinearGradient(0, h * 0.35, 0, h);
    g.addColorStop(0, "#1b2935");
    g.addColorStop(1, "#0a1018");
    cx!.fillStyle = g;
    cx!.fill();
    // Lighter rim along the surface.
    cx!.beginPath();
    cx!.moveTo(0, floorY(0));
    for (let x = 0; x <= w; x += 8) cx!.lineTo(x, floorY(x));
    cx!.strokeStyle = "rgba(90, 120, 140, 0.4)";
    cx!.lineWidth = 1.5;
    cx!.stroke();
    // Stones resting on the floor.
    for (const st of stones) {
      const sx = st.frac * w;
      const sy = floorY(sx) - st.rh * 0.35;
      const r = (52 + st.shade * 30) | 0;
      const gg = (60 + st.shade * 32) | 0;
      const bb = (72 + st.shade * 36) | 0;
      cx!.save();
      cx!.translate(sx, sy);
      cx!.rotate(st.rot);
      cx!.beginPath();
      cx!.ellipse(0, 0, st.rw, st.rh, 0, 0, Math.PI * 2);
      cx!.fillStyle = `rgb(${r},${gg},${bb})`;
      cx!.fill();
      cx!.restore();
    }
  }

  function drawCritters(dt: number): void {
    for (const c of critters) {
      c.timer -= dt;
      if (c.timer <= 0) {
        c.moving = !c.moving;
        if (Math.random() < 0.4) c.dir *= -1;
        c.gait = 0.7 + Math.random() * 0.8;
        c.timer = c.moving
          ? 0.25 + Math.random() * 0.9
          : 0.15 + Math.random() * 0.6;
      }
      if (c.moving) {
        c.x += c.dir * c.speed * c.gait * dt;
        c.leg += dt * 18 * c.gait;
      }
      if (c.x < -10) c.x = w + 10;
      else if (c.x > w + 10) c.x = -10;
      const fy = floorY(c.x) - 1.5;
      const wob = Math.sin(c.leg) * 0.6;
      cx!.save();
      cx!.translate(c.x, fy + wob);
      cx!.scale(c.dir, 1);
      cx!.beginPath();
      cx!.ellipse(0, 0, c.len, c.len * 0.55, 0, 0, Math.PI * 2);
      cx!.fillStyle = "rgba(15, 18, 22, 0.8)";
      cx!.fill();
      cx!.strokeStyle = "rgba(15, 18, 22, 0.5)";
      cx!.lineWidth = 0.6;
      cx!.beginPath();
      cx!.moveTo(-c.len * 0.4, c.len * 0.4);
      cx!.lineTo(-c.len * 0.4, c.len * 0.4 + 1.4 + wob);
      cx!.moveTo(c.len * 0.4, c.len * 0.4);
      cx!.lineTo(c.len * 0.4, c.len * 0.4 + 1.4 - wob);
      cx!.stroke();
      cx!.restore();
    }
  }

  size();

  if (prefersReducedMotion()) {
    drawFloor();
    window.addEventListener("resize", () => {
      size();
      drawFloor();
    });
    return noop;
  }

  buildCritters();
  window.addEventListener("resize", () => {
    size();
    buildCritters();
  });

  let raf = 0;
  let last = 0;
  let running = false;
  function frame(now: number): void {
    if (!running) return;
    const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
    last = now;
    drawFloor();
    drawCritters(dt);
    raf = requestAnimationFrame(frame);
  }
  return {
    start() {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(frame);
    },
    stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    },
  };
}

// Run a canvas controller's RAF loop only while the canvas is on screen.
function runWhileVisible(el: Element, ctrl: BubbleController): void {
  if (typeof IntersectionObserver !== "function") {
    ctrl.start();
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) ctrl.start();
        else ctrl.stop();
      }
    },
    { threshold: 0.02 },
  );
  io.observe(el);
}

// Mount everything: reveal each `.depth` section, wire scroll buttons, and run
// each bubble / floor canvas only while it's on screen.
export function mountDepthSections(): void {
  document
    .querySelectorAll<HTMLElement>(".depth")
    .forEach((s) => revealOnView(s));
  wireScrollButtons(document);

  document
    .querySelectorAll<HTMLCanvasElement>("[data-bubbles]")
    .forEach((canvas) => runWhileVisible(canvas, createBubbles(canvas)));

  document
    .querySelectorAll<HTMLCanvasElement>("[data-floor]")
    .forEach((canvas) => runWhileVisible(canvas, createOceanFloor(canvas)));
}
